import { EventEmitter } from 'events';

export interface CalWebSocketMessage {
  type: 'status' | 'chunk' | 'complete' | 'error' | 'timeout' |
        'batchUploadStatus' | 'batchUploadProgress' | 'batchUploadComplete' | 'batchUploadError' |
        'pdf_generated';  // Added PDF generation type
  content?: string;
  message?: string;
  response?: string;  // For complete responses
  partial_response?: string;
  execution_time?: number;
  progress?: number;
  completed?: number;
  total?: number;
  currentFile?: string;
  uploadedFiles?: any[];
  uploadTimestamp?: string;
  fileIndex?: number;
  fileName?: string;
  timestamp: string;
  // Added for async document analysis
  analysis_complete?: boolean;
  document_name?: string;
  // Added for async queries
  queryId?: string;
  status?: string;
  // Added for PDF generation
  pdf_generated?: boolean;
  report_id?: string;
  title?: string;
  report_type?: 'checklist' | 'gap_analysis' | 'audit' | 'procedure';
  pdf_url?: string;
  metadata?: {
    file_size: number;
    pages: number;
    generated_at: string;
  };
}

export interface CalWebSocketOptions {
  token: string;
  onMessage?: (message: CalWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

export class CalWebSocketService extends EventEmitter {
  private ws: WebSocket | null = null;
  private reconnectTimer?: NodeJS.Timeout;
  private pingInterval?: NodeJS.Timeout;
  private messageQueue: string[] = [];
  private token: string;
  private wsUrl: string;
  private isConnecting: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 5000;

  constructor(options: CalWebSocketOptions) {
    super();
    this.token = options.token;

    // Get WebSocket URL from environment or use default
    const wsEndpoint = process.env.REACT_APP_WS_ENDPOINT || 'wss://8yvd34whei.execute-api.us-east-2.amazonaws.com/prod';
    this.wsUrl = `${wsEndpoint}?token=${encodeURIComponent(this.token)}`;

    // Set up event listeners
    if (options.onMessage) this.on('message', options.onMessage);
    if (options.onConnect) this.on('connected', options.onConnect);
    if (options.onDisconnect) this.on('disconnected', options.onDisconnect);
    if (options.onError) this.on('error', options.onError);
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected()) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Already connecting, wait for it
        const checkConnection = setInterval(() => {
          if (this.isConnected()) {
            clearInterval(checkConnection);
            resolve();
          } else if (!this.isConnecting) {
            clearInterval(checkConnection);
            reject(new Error('Connection failed'));
          }
        }, 100);
        return;
      }

      this.isConnecting = true;
      console.log('Connecting to Cal WebSocket...');

      try {
        this.ws = new WebSocket(this.wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.startPingInterval();
          this.processMessageQueue();
          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as CalWebSocketMessage;
            console.log('WebSocket message:', data.type);
            this.emit('message', data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          this.emit('error', error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.isConnecting = false;
          this.stopPingInterval();

          // Only attempt reconnect if not a normal closure
          if (event.code !== 1000 && event.code !== 1001) {
            this.scheduleReconnect();
          }

          this.emit('disconnected');
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * The SAME session id the REST path uses (`cal-unified-session-id`, set by CalChatContext).
   *
   * Bedrock Agents keep conversation history per sessionId, so this is what makes Cal remember
   * the previous turn. Until now the WebSocket handler generated its own id per message --
   * `ws-{connection}-{timestamp}` -- which meant EVERY message started a brand-new conversation:
   * ask about August, ask a follow-up, and Cal had no idea what "it" referred to; Cal would offer
   * to "drill into this" and "yes" meant nothing.
   *
   * Reading the same key the REST path uses also keeps ONE thread when a conversation moves
   * between the two transports, which it does for longer queries.
   */
  private getSessionId(): string {
    try {
      let id = sessionStorage.getItem('cal-unified-session-id');
      if (!id) {
        id = `cal-unified-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('cal-unified-session-id', id);
      }
      return id;
    } catch {
      // private browsing / blocked storage: fall back to a per-tab id so the socket still works
      return `cal-unified-fallback-${Date.now()}`;
    }
  }

  /**
   * `sessionIdOverride` lets a surface keep its OWN Bedrock conversation instead of the shared
   * one. The Month End Checklist uses it so its chat is about the close and nothing else -- the
   * backend keys agent history on this id (message_handler.py reads the top-level sessionId).
   */
  sendMessage(message: string, context?: any, sessionIdOverride?: string): void {
    const payload = {
      action: 'sendMessage',
      message,
      context,
      sessionId: sessionIdOverride || this.getSessionId(),
      timestamp: new Date().toISOString()
    };

    const payloadStr = JSON.stringify(payload);

    if (this.isConnected()) {
      console.log('Sending message via WebSocket');
      this.ws!.send(payloadStr);
    } else {
      console.log('Queueing message, not connected');
      // Queue message if not connected
      this.messageQueue.push(payloadStr);
      // Try to connect
      this.connect().catch(console.error);
    }
  }

  sendBatchUpload(files: Array<{fileName: string, data: string}>, userEmail: string): void {
    const payload = {
      action: 'batchUpload',
      files,
      userEmail,
      timestamp: new Date().toISOString()
    };

    const payloadStr = JSON.stringify(payload);

    if (this.isConnected()) {
      console.log('Sending batch upload via WebSocket');
      this.ws!.send(payloadStr);
    } else {
      console.log('Queueing batch upload, not connected');
      // Queue message if not connected
      this.messageQueue.push(payloadStr);
      // Try to connect
      this.connect().catch(console.error);
    }
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        console.log('Sending ping');
        this.ws!.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      this.emit('error', new Error('Max reconnection attempts reached'));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 30000);

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.connect().catch(console.error);
    }, delay);
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.isConnected()) {
      const message = this.messageQueue.shift()!;
      console.log('Sending queued message');
      this.ws!.send(message);
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  disconnect(): void {
    console.log('Disconnecting WebSocket');
    this.stopPingInterval();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.messageQueue = [];
    this.reconnectAttempts = 0;
  }

  updateToken(newToken: string): void {
    this.token = newToken;
    this.wsUrl = `${process.env.REACT_APP_WS_ENDPOINT || 'wss://8yvd34whei.execute-api.us-east-2.amazonaws.com/prod'}?token=${encodeURIComponent(newToken)}`;

    // Reconnect with new token
    this.disconnect();
    this.connect().catch(console.error);
  }
}